// Tipos de usuário
export interface User {
  id: number;
  email: string;
  nome: string;
  telefone?: string;
  nascimento?: string;
  cpf_bi?: string | null;
  avatar?: string;
  last_login?: string;
  cep?: string | null;
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  status: number;
  permissao: {
    id: number;
    permission: string | null;
  };
  // Campos legados para compatibilidade
  role?: UserRole;
  permissaos_id?: number | string;
  /** Páginas permitidas (Gestão de Roles) — vem do login/profile */
  paginaList?: string[];
  phone?: string;
  created_at?: string;
  updated_at?: string;
}

export type UserRole = 'admin' | 'manager' | 'driver' | 'customer';

// Sistema de permissões baseado no permissao_id
export const PERMISSAO_MAP = {
  1: 'Admin',
  2: 'Gerente',
  3: 'Funcionário',
  4: 'Cliente',
  5: 'Fornecedor',
  6: 'Contador'
} as const;

export type PermissaoId = keyof typeof PERMISSAO_MAP;
export type PermissaoNome = typeof PERMISSAO_MAP[PermissaoId];

// Tipos de autenticação
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  refresh_token?: string;
}

export interface ApiLoginResponse {
  authorisation: {
    token: string;
  };
  user: User;
  refresh_token?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Tipos de carga
export interface Shipment {
  id: string;
  tracking_number: string;
  origin: string;
  destination: string;
  status: ShipmentStatus;
  weight: number;
  dimensions: Dimensions;
  description?: string;
  priority: Priority;
  customer_id?: string;
  driver_id?: string;
  vehicle_id?: string;
  created_at: string;
  updated_at: string;
  estimated_delivery?: string;
  actual_delivery?: string;
}

export type ShipmentStatus =
  | 'pending'
  | 'confirmed'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled'
  | 'returned';

export type Priority = 'low' | 'medium' | 'high' | 'urgent';

export interface Dimensions {
  length: number;
  width: number;
  height: number;
  unit?: 'cm' | 'in';
}

// Tipos de veículo
export interface Vehicle {
  id: string;
  plate: string;
  model: string;
  brand: string;
  year: number;
  capacity: number;
  fuel_type: FuelType;
  driver_id?: string;
  status: VehicleStatus;
  created_at: string;
  updated_at: string;
}

export type FuelType = 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'ethanol';
export type VehicleStatus = 'available' | 'in_use' | 'maintenance' | 'out_of_service';

// Tipos de rastreamento
export interface TrackingInfo {
  tracking_number: string;
  status: ShipmentStatus;
  current_location: string;
  estimated_delivery: string;
  history: TrackingEvent[];
}

export interface TrackingEvent {
  timestamp: string;
  status: ShipmentStatus;
  location: string;
  description: string;
  notes?: string;
}

// Tipos de relatórios
export interface ReportFilters {
  start_date: string;
  end_date: string;
  status?: ShipmentStatus;
  driver_id?: string;
  vehicle_id?: string;
}

export interface DeliveryReport {
  total_shipments: number;
  delivered: number;
  in_transit: number;
  pending: number;
  cancelled: number;
  average_delivery_time: number;
  revenue: number;
  period: string;
}

// Tipos de API
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface ApiError {
  message: string;
  error: string;
  status_code: number;
  details?: any;
}

// Tipos de formulários
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea' | 'checkbox';
  required: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    custom?: (value: any) => string | null;
  };
}

// Tipos de notificações
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  action_url?: string;
}

// Tipos de configuração
export interface AppConfig {
  api: {
    base_url: string;
    timeout: number;
    retry_attempts: number;
  };
  app: {
    name: string;
    version: string;
    environment: string;
  };
  features: {
    dark_mode: boolean;
    notifications: boolean;
    analytics: boolean;
  };
}

// Tipos para Roles e Permissões
export interface Permission {
  id: number;
  name: string;
  description: string;
  module: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: Permission[];
  created_at?: string;
  updated_at?: string;
}

export interface RoleFormData {
  name: string;
  description: string;
  permissionIds: number[];
}

// Módulos disponíveis no sistema
export const SYSTEM_MODULES = {
  DASHBOARD: 'dashboard',
  CLIENTS: 'clients',
  INVOICES: 'invoices',
  PACKAGES: 'packages',
  USERS: 'users',
  SETTINGS: 'settings',
  PROFILE: 'profile',
} as const;

// Permissões padrão do sistema
export const DEFAULT_PERMISSIONS: Permission[] = [
  { id: 1, name: 'Acesso ao dashboard principal', description: 'Visualizar o painel principal', module: SYSTEM_MODULES.DASHBOARD },
  { id: 2, name: 'Gerenciar clientes', description: 'Criar, editar e excluir clientes', module: SYSTEM_MODULES.CLIENTS },
  { id: 3, name: 'Gerenciar faturas', description: 'Criar, editar e excluir faturas', module: SYSTEM_MODULES.INVOICES },
  { id: 4, name: 'Gerenciar pacotes', description: 'Criar, editar e excluir pacotes', module: SYSTEM_MODULES.PACKAGES },
  { id: 5, name: 'Gerenciar usuários', description: 'Criar, editar e excluir usuários', module: SYSTEM_MODULES.USERS },
  { id: 6, name: 'Acessar configurações', description: 'Visualizar e modificar configurações', module: SYSTEM_MODULES.SETTINGS },
  { id: 7, name: 'Gerenciar roles', description: 'Criar, editar e excluir roles de acesso', module: SYSTEM_MODULES.SETTINGS },
  { id: 8, name: 'Acessar perfil', description: 'Visualizar e editar próprio perfil', module: SYSTEM_MODULES.PROFILE },
];
